// src/pages/Buyer/Payment.jsx
// ✅ COMPLETE: Split payment stages, upload restriction, payment terms support
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Divider, Chip, Stepper, Step, StepLabel, StepContent,
  LinearProgress,
} from '@mui/material';
import {
  AttachMoney, Upload, CheckCircle, ArrowBack, CloudUpload,
  Receipt, Warning, Lock, HourglassEmpty, MonetizationOn,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, paymentAPI } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const STAGE_ICONS = [
  <MonetizationOn />,
  <AttachMoney />,
  <CheckCircle />,
];

const BuyerPayment = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();

  const [invoice,        setInvoice]       = useState(null);
  const [stages,         setStages]        = useState([]);
  const [terms,          setTerms]         = useState('full_100');
  const [loading,        setLoading]       = useState(true);
  const [activeStage,    setActiveStage]   = useState(0);   // which stage is expanded
  const [submitting,     setSubmitting]    = useState(false);
  const [submitted,      setSubmitted]     = useState(false);
  const [submittedStage, setSubmittedStage]= useState(null);

  // per-stage form state
  const [paymentProof,   setPaymentProof]  = useState(null);
  const [refNumber,      setRefNumber]     = useState('');
  const [paymentMethod,  setPaymentMethod] = useState('bank_transfer');
  const [notes,          setNotes]         = useState('');

  useEffect(() => {
    fetchAll();
  }, [enquiryId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Load invoice info
      let inv = null;
      try {
        const r = await userAPI.getInvoiceById(enquiryId);
        inv = r.data;
      } catch {
        try {
          const r2 = await userAPI.getMyInvoices();
          const list = Array.isArray(r2.data) ? r2.data : [];
          inv = list.find(i => String(i.id) === String(enquiryId) || String(i.enquiry_id) === String(enquiryId)) || null;
        } catch { /* ignore */ }
      }
      setInvoice(inv);

      // Load payment stages
      try {
        const r3 = await paymentAPI.getTerms(enquiryId);
        setStages(r3.data.stages || []);
        setTerms(r3.data.terms || 'full_100');
        // Auto-select first unpaid stage
        const firstUnpaid = (r3.data.stages || []).findIndex(s => !s.already_paid);
        setActiveStage(firstUnpaid >= 0 ? firstUnpaid : 0);
      } catch {
        // Fallback: full payment
        const total = inv?.totalAmount || inv?.total_amount || 0;
        setStages([{ stage: 1, label: 'Full Payment', pct: 100, type: 'full',
                     description: 'Full payment before shipment', amount: total, already_paid: false }]);
        setTerms('full_100');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG, GIF or PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10 MB'); return; }
    setPaymentProof(file);
    toast.success(`${file.name} selected`);
  };

  const handleSubmit = async (stage) => {
    if (!paymentProof) { toast.error('Please upload payment proof first'); return; }
    if (stage.already_paid) { toast.error('Payment already submitted for this stage'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('payment_proof',  paymentProof);
      fd.append('enquiry_id',     enquiryId);
      fd.append('stage_number',   stage.stage);
      fd.append('stage_label',    stage.label);
      fd.append('payment_type',   stage.type);
      fd.append('payment_method', paymentMethod);
      fd.append('transaction_id', refNumber);
      fd.append('amount',         stage.amount);
      fd.append('notes',          notes);

      await paymentAPI.submitStage(fd);

      setSubmittedStage(stage);
      toast.success(`Stage ${stage.stage} payment submitted! Our team will verify within 24h.`);
      // Clear the form fields for the next stage
      setPaymentProof(null);
      setRefNumber('');
      setNotes('');
      // Refresh stages — marks this stage as already_paid and moves to next
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.data?.error || err?.message || 'Submission failed';
      if (err?.response?.data?.already_uploaded || err?.data?.already_uploaded) {
        toast.error('Payment proof already uploaded for this stage!');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Container sx={{ py: 6, textAlign: 'center' }}>
      <CircularProgress size={48} />
      <Typography sx={{ mt: 2 }} color="text.secondary">Loading payment details...</Typography>
    </Container>
  );

  // No full-page success screen — inline banner shown inside the stages view

  const totalAmount = invoice?.totalAmount || invoice?.total_amount || 0;
  const invoiceNum  = invoice?.invoiceNumber || invoice?.invoice_number || `INV-${enquiryId}`;
  const product     = invoice?.product || invoice?.productName || 'Fruit Purchase';
  const paidCount    = stages.filter(s => s.already_paid).length;
  const submittedCount = stages.filter(s => s.submitted).length;
  const progress    = stages.length > 0 ? (paidCount / stages.length) * 100 : 0;

  const TERMS_LABELS = {
    'full_100':  'Full Payment',
    '30_70':     'Split: 30% + 70%',
    '40_30_30':  'Split: 40% + 30% + 30%',
    '25_51_24':  'Trusted Buyer: 25% + 51% + 24%',
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/buyer/invoices')} sx={{ mb: 2 }}>
          Back to Invoices
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Pay Invoice</Typography>
            <Typography color="text.secondary">Upload payment proof for each stage</Typography>
          </Box>
          <Chip
            label={TERMS_LABELS[terms] || terms}
            color="primary"
            variant="outlined"
            icon={<Receipt />}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>

        {/* LEFT: Invoice Summary + Progress */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2, position: 'sticky', top: 20 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Receipt color="primary" />
              <Typography variant="h6" fontWeight="bold">Invoice Summary</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {[
              ['Invoice No.',  invoiceNum],
              ['Enquiry',      `#${enquiryId}`],
              ['Product',      product],
              ['Issue Date',   formatDate(invoice?.issueDate || invoice?.issue_date)],
            ].map(([label, value]) => value ? (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{label}:</Typography>
                <Typography variant="body2" fontWeight="medium">{value}</Typography>
              </Box>
            ) : null)}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Total</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(totalAmount)}
              </Typography>
            </Box>

            {/* Payment progress */}
            {stages.length > 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Payment Progress ({paidCount}/{stages.length} stages)
                </Typography>
                <LinearProgress variant="determinate" value={progress}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }} color={progress === 100 ? 'success' : 'primary'} />
                <Typography variant="caption" color={progress === 100 ? 'success.main' : 'text.secondary'}>
                  {progress === 100 ? '✅ All stages paid' : `${Math.round(progress)}% complete`}
                </Typography>
              </>
            )}

            {/* Stage summary chips */}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {stages.map(s => (
                <Box key={s.stage} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{s.label}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="medium">{formatCurrency(s.amount)}</Typography>
                    {s.already_paid
                      ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      : s.submitted
                        ? <HourglassEmpty sx={{ fontSize: 16, color: 'warning.main' }} />
                        : <HourglassEmpty sx={{ fontSize: 16, color: 'text.disabled' }} />
                    }
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: Payment Stages */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Payment Stages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete each stage by uploading your payment proof. Each stage requires a separate upload.
            </Typography>

            {/* Inline success banner after stage submission */}
            {submittedStage && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}
                onClose={() => setSubmittedStage(null)}>
                <strong>Stage {submittedStage.stage} ({submittedStage.label})</strong> proof submitted!
                Admin will verify within 24 hours.
                {stages.filter(s => !s.already_paid).length > 0 && (
                  <> You can now proceed with the next payment stage below.</>
                )}
              </Alert>
            )}

            <Stepper orientation="vertical" activeStep={activeStage} nonLinear>
              {stages.map((stage, idx) => (
                <Step key={stage.stage} completed={stage.already_paid}>
                  <StepLabel
                    onClick={() => !stage.already_paid && setActiveStage(idx)}
                    sx={{ cursor: stage.already_paid ? 'default' : 'pointer' }}
                    icon={stage.already_paid
                      ? <CheckCircle color="success" />
                      : stage.submitted
                        ? <HourglassEmpty color="warning" />
                        : undefined}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography fontWeight="medium">
                        Stage {stage.stage}: {stage.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrency(stage.amount)}
                        </Typography>
                        <Chip size="small"
                          label={stage.already_paid ? '✅ Verified' : stage.submitted ? '⏳ Awaiting Verification' : 'Pending'}
                          color={stage.already_paid ? 'success' : stage.submitted ? 'warning' : 'default'}
                        />
                      </Box>
                    </Box>
                  </StepLabel>
                  <StepContent>
                    {stage.already_paid ? (
                      <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                        <strong>Stage {stage.stage} Verified!</strong> Admin has confirmed this payment.
                      </Alert>
                    ) : stage.submitted ? (
                      <Alert severity="warning" icon={<HourglassEmpty />} sx={{ mb: 2 }}>
                        <strong>Proof submitted — awaiting admin verification.</strong><br />
                        You can re-upload if you need to replace the document.
                      </Alert>
                    ) : (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <strong>{stage.description}</strong><br />
                          Amount: <strong>{formatCurrency(stage.amount)}</strong> ({stage.pct}% of total)
                        </Alert>

                        {/* File Upload */}
                        <Box sx={{
                          border: '2px dashed',
                          borderColor: paymentProof && activeStage === idx ? 'success.main' : 'primary.light',
                          borderRadius: 2, p: 3, textAlign: 'center',
                          bgcolor: paymentProof && activeStage === idx ? '#f0fff4' : 'background.default',
                          mb: 2, cursor: 'pointer',
                          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                        }}>
                          <input accept=".jpg,.jpeg,.png,.gif,.pdf" style={{ display: 'none' }}
                            id={`proof-upload-${stage.stage}`} type="file"
                            onChange={handleFileChange} />
                          <label htmlFor={`proof-upload-${stage.stage}`} style={{ cursor: 'pointer', display: 'block' }}>
                            {paymentProof && activeStage === idx ? (
                              <>
                                <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                <Typography color="success.main" fontWeight="bold">{paymentProof.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(paymentProof.size / 1024).toFixed(1)} KB — Click to change
                                </Typography>
                              </>
                            ) : (
                              <>
                                <CloudUpload sx={{ fontSize: 40, color: 'primary.light', mb: 1 }} />
                                <Typography fontWeight="bold">Upload Payment Proof</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Bank receipt, transfer advice, or screenshot (Max 10 MB)
                                </Typography>
                              </>
                            )}
                          </label>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Payment Method</InputLabel>
                              <Select value={paymentMethod} label="Payment Method"
                                onChange={(e) => setPaymentMethod(e.target.value)}>
                                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                <MenuItem value="international_transfer">International Wire (SWIFT)</MenuItem>
                                <MenuItem value="upi">UPI</MenuItem>
                                <MenuItem value="cheque">Cheque</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small"
                              label="Transaction / Reference No."
                              value={refNumber}
                              onChange={(e) => setRefNumber(e.target.value)}
                              placeholder="UTR / NEFT / SWIFT ref" />
                          </Grid>
                        </Grid>

                        <TextField fullWidth size="small" multiline rows={2}
                          label="Notes (optional)"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          sx={{ mb: 2 }} />

                        {!paymentProof && (
                          <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                            Upload payment proof document to proceed.
                          </Alert>
                        )}

                        <Button variant="contained" color="success" size="large" fullWidth
                          onClick={() => handleSubmit(stage)}
                          disabled={!paymentProof || submitting}
                          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                          sx={{ py: 1.5, fontWeight: 'bold' }}>
                          {submitting
                            ? 'Submitting...'
                            : `Submit Stage ${stage.stage} — ${formatCurrency(stage.amount)}`}
                        </Button>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {stages.every(s => s.already_paid) && (
              <Alert severity="success" sx={{ mt: 3 }} icon={<CheckCircle />}>
                <strong>All payment stages submitted!</strong> Admin will verify and confirm shipment.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BuyerPayment;
